import React, { useEffect, useRef, useState } from "react";
import { Camera, Save, User, IdCard, Users2, X, LoaderCircle, FileSignature, Plus, Trash2, Star, Phone } from "lucide-react";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { paises, grausDependencia, grausParentesco } from "../mock/paises";
import { maskCPF, maskRG, maskCNS, maskCEP } from "../utils/masks";

const emptyFamiliar = () => ({
  id: "fam_" + Math.random().toString(36).slice(2, 8),
  nome: "", parentesco: "", cpf: "", rg: "", telefone: "", email: "",
  responsavelFinanceiro: false, contatoEmergencia: false,
  endCep: "", endLogradouro: "", endNumero: "", endComplemento: "",
  endBairro: "", endCidade: "", endUf: "",
});

const emptyForm = {
  nome: "", quarto: "", dataInternacao: "", dataNascimento: "", grauDependencia: "1", foto: "",
  cpf: "", cns: "", convenio: "", carteirinhaConvenio: "",
  nacionalidade: "Brasil", rg: "", orgaoEmissor: "", dataEmissaoRg: "", naturalidade: "", sexo: "",
  nomePai: "", nomeMae: "",
  valorMensalidade: "", dataInicioContrato: "", cobrarDecimoTerceiro: false, descontoPercentual: "",
  diaPagamento: "10", periodicidadePagamento: "mensal", fechamentoDiasAntes: "", formaCobranca: "boleto",
  familiares: [],
};

const Field = ({ label, children }) => (
  <div>
    <label className="text-sm font-medium text-slate-600 mb-1 block">{label}</label>
    {children}
  </div>
);

const inputCls =
  "w-full px-3.5 py-2.5 rounded-lg border border-slate-200 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 text-sm";

const ResidenteFormDialog = ({ open, onOpenChange, onSave, initial }) => {
  const [form, setForm] = useState(emptyForm);
  const [cepLoading, setCepLoading] = useState({});
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      const base = { ...emptyForm, ...(initial || {}) };
      if (!base.familiares || base.familiares.length === 0) {
        const f = emptyFamiliar();
        f.responsavelFinanceiro = true;
        f.contatoEmergencia = true;
        base.familiares = [f];
      }
      setForm(base);
    }
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => set("foto", reader.result);
    reader.readAsDataURL(file);
  };

  // ------ Familiares ------
  const setFam = (i, k, v) =>
    setForm((f) => ({ ...f, familiares: f.familiares.map((fam, idx) => (idx === i ? { ...fam, [k]: v } : fam)) }));

  const addFamiliar = () => setForm((f) => ({ ...f, familiares: [...f.familiares, emptyFamiliar()] }));

  const removeFamiliar = (i) =>
    setForm((f) => ({ ...f, familiares: f.familiares.filter((_, idx) => idx !== i) }));

  const setUnico = (i, key) =>
    setForm((f) => ({ ...f, familiares: f.familiares.map((fam, idx) => ({ ...fam, [key]: idx === i })) }));

  const buscarCepFamiliar = async (i, cep) => {
    const clean = (cep || "").replace(/\D/g, "");
    if (clean.length !== 8) return;
    setCepLoading((s) => ({ ...s, [i]: true }));
    try {
      const r = await fetch(`https://viacep.com.br/ws/${clean}/json/`);
      const d = await r.json();
      if (!d.erro) {
        setForm((f) => ({
          ...f,
          familiares: f.familiares.map((fam, idx) =>
            idx === i
              ? { ...fam, endLogradouro: d.logradouro || fam.endLogradouro, endBairro: d.bairro || fam.endBairro, endCidade: d.localidade || fam.endCidade, endUf: d.uf || fam.endUf }
              : fam),
        }));
      }
    } catch (e) { /* silencioso */ }
    setCepLoading((s) => ({ ...s, [i]: false }));
  };

  const handleSave = () => {
    if (!form.nome) return;
    const familiares = form.familiares.filter((f) => f.nome.trim());
    onSave({ ...form, valorMensalidade: Number(form.valorMensalidade) || 0,
      fechamentoDiasAntes: (form.fechamentoDiasAntes === "" || form.fechamentoDiasAntes === null || form.fechamentoDiasAntes === undefined) ? null : (parseInt(form.fechamentoDiasAntes, 10) || 0),
      familiares });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{initial?.id ? "Editar Residente" : "Novo Residente"}</DialogTitle>
        </DialogHeader>

        {/* Foto */}
        <div className="flex items-center gap-4 pb-2">
          <div className="relative">
            <div className="w-20 h-20 rounded-2xl bg-slate-100 overflow-hidden flex items-center justify-center border border-slate-200">
              {form.foto ? (
                <img src={form.foto} alt="foto" className="w-full h-full object-cover" />
              ) : (
                <User className="w-8 h-8 text-slate-300" />
              )}
            </div>
            {form.foto && (
              <button onClick={() => set("foto", "")} className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFoto} />
            <Button variant="outline" className="gap-2" onClick={() => fileRef.current?.click()}>
              <Camera className="w-4 h-4" /> Enviar foto
            </Button>
            <p className="text-xs text-slate-400 mt-1.5">JPG ou PNG do residente</p>
          </div>
        </div>

        <Tabs defaultValue="pessoais" className="w-full">
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="pessoais" className="gap-1.5"><User className="w-4 h-4" /> Pessoais</TabsTrigger>
            <TabsTrigger value="documentos" className="gap-1.5"><IdCard className="w-4 h-4" /> Documentos</TabsTrigger>
            <TabsTrigger value="contrato" className="gap-1.5"><FileSignature className="w-4 h-4" /> Contrato</TabsTrigger>
            <TabsTrigger value="familia" className="gap-1.5"><Users2 className="w-4 h-4" /> Família</TabsTrigger>
          </TabsList>

          {/* Pessoais */}
          <TabsContent value="pessoais" className="space-y-3.5 pt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <Field label="Nome completo"><input data-testid="residente-nome" className={inputCls} value={form.nome} onChange={(e) => set("nome", e.target.value)} /></Field>
              <Field label="Quarto"><input data-testid="residente-quarto" className={inputCls} value={form.quarto} onChange={(e) => set("quarto", e.target.value)} /></Field>
              <Field label="Data de internação"><input type="date" className={inputCls} value={form.dataInternacao} onChange={(e) => set("dataInternacao", e.target.value)} /></Field>
              <Field label="Data de nascimento"><input type="date" data-testid="residente-nascimento" className={inputCls} value={form.dataNascimento} onChange={(e) => set("dataNascimento", e.target.value)} /></Field>
              <Field label="Grau de dependência">
                <Select value={form.grauDependencia} onValueChange={(v) => set("grauDependencia", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{grausDependencia.map((g) => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Nacionalidade">
                <Select value={form.nacionalidade} onValueChange={(v) => set("nacionalidade", v)}>
                  <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                  <SelectContent className="max-h-72">{paises.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Naturalidade (cidade/UF)"><input className={inputCls} value={form.naturalidade} onChange={(e) => set("naturalidade", e.target.value)} /></Field>
              <Field label="Sexo">
                <Select value={form.sexo || ""} onValueChange={(v) => set("sexo", v)}>
                  <SelectTrigger data-testid="residente-sexo"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Feminino">Feminino</SelectItem>
                    <SelectItem value="Masculino">Masculino</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Nome do Pai"><input className={inputCls} value={form.nomePai} onChange={(e) => set("nomePai", e.target.value)} /></Field>
              <Field label="Nome da Mãe"><input className={inputCls} value={form.nomeMae} onChange={(e) => set("nomeMae", e.target.value)} /></Field>
            </div>
          </TabsContent>

          {/* Documentos */}
          <TabsContent value="documentos" className="space-y-3.5 pt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <Field label="CPF"><input className={inputCls} value={form.cpf} placeholder="000.000.000-00" onChange={(e) => set("cpf", maskCPF(e.target.value))} /></Field>
              <Field label="CNS (Cartão Nacional de Saúde)"><input className={inputCls} value={form.cns} placeholder="00000000000 0000 0" maxLength={18} onChange={(e) => set("cns", maskCNS(e.target.value))} data-testid="residente-cns" /></Field>
              <Field label="RG"><input className={inputCls} value={form.rg} placeholder="00.000.000-0" onChange={(e) => set("rg", maskRG(e.target.value))} /></Field>
              <Field label="Órgão Emissor"><input className={inputCls} value={form.orgaoEmissor} placeholder="SSP/SP" onChange={(e) => set("orgaoEmissor", e.target.value)} /></Field>
              <Field label="Data de Emissão (RG)"><input type="date" className={inputCls} value={form.dataEmissaoRg} onChange={(e) => set("dataEmissaoRg", e.target.value)} /></Field>
              <Field label="Convênio"><input className={inputCls} value={form.convenio} onChange={(e) => set("convenio", e.target.value)} /></Field>
              <Field label="Carteirinha do Convênio"><input className={inputCls} value={form.carteirinhaConvenio} onChange={(e) => set("carteirinhaConvenio", e.target.value)} /></Field>
            </div>
          </TabsContent>

          {/* Contrato */}
          <TabsContent value="contrato" className="space-y-3.5 pt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <Field label="Valor da mensalidade atual (R$)">
                <input type="number" step="0.01" className={inputCls} value={form.valorMensalidade} placeholder="0,00" onChange={(e) => set("valorMensalidade", e.target.value)} />
              </Field>
              <Field label="Data de início do contrato">
                <input type="date" className={inputCls} value={form.dataInicioContrato} onChange={(e) => set("dataInicioContrato", e.target.value)} />
              </Field>
              <Field label="Desconto na mensalidade (%) — opcional">
                <input type="number" step="0.01" min="0" max="100" className={inputCls} value={form.descontoPercentual ?? ""} placeholder="0"
                  onChange={(e) => set("descontoPercentual", e.target.value)} data-testid="res-desconto-percentual" />
                <p className="text-[11px] text-slate-400 mt-1">Na fatura aparece só a linha "Desconto na mensalidade" com o valor em R$ — o percentual não é exibido.</p>
              </Field>
              <Field label="Dia de vencimento da mensalidade">
                <input type="number" min="1" max="31" className={inputCls} value={form.diaPagamento}
                  onChange={(e) => set("diaPagamento", e.target.value)} placeholder="Ex.: 10" data-testid="res-dia-pagamento" />
              </Field>
              <Field label="Periodicidade">
                <Select value={form.periodicidadePagamento} onValueChange={(v) => set("periodicidadePagamento", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[["mensal", "Mensal"], ["trimestral", "Trimestral"], ["semestral", "Semestral"], ["anual", "Anual"]]
                      .map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Fechamento: dias antes do pagamento (opcional)">
                <input type="number" min="0" max="27" className={inputCls} value={form.fechamentoDiasAntes ?? ""}
                  onChange={(e) => set("fechamentoDiasAntes", e.target.value)} placeholder="Padrão global (ex.: 5)" data-testid="res-fechamento-dias" />
              </Field>
              <Field label="Forma de cobrança">
                <Select value={form.formaCobranca || "boleto"} onValueChange={(v) => set("formaCobranca", v)}>
                  <SelectTrigger data-testid="res-forma-cobranca"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="boleto" data-testid="res-forma-cobranca-boleto">Boleto automático (Asaas)</SelectItem>
                    <SelectItem value="pix" data-testid="res-forma-cobranca-pix">Pix manual (sem boleto automático)</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <div className="text-sm text-slate-500 bg-blue-50/70 border border-blue-100 rounded-lg px-4 py-3">
              Informe o <b>valor ATUAL</b> da mensalidade (já reajustado). O reajuste de <b>8%</b> incide a cada <b>12 meses</b> no <b>aniversário do contrato</b>: o próximo reajuste será calculado a partir da data de início, sem alterar o valor atual até lá. A fatura é fechada <b>5 dias antes</b> do dia de pagamento e enviada ao responsável financeiro. O responsável financeiro (aba Família) será o <b>contratante</b> no contrato impresso.
            </div>
            <label className="flex items-start gap-2.5 p-3 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-50" data-testid="cobrar-decimo-toggle">
              <input type="checkbox" className="mt-0.5 w-4 h-4 accent-blue-600" checked={!!form.cobrarDecimoTerceiro} onChange={(e) => set("cobrarDecimoTerceiro", e.target.checked)} />
              <span className="text-sm text-slate-600"><b>Cobrar 13º salário</b> — na fatura de dezembro será somado o 13º proporcional aos meses de residência no ano (mensalidade ÷ 12 × nº de meses), junto com a mensalidade e os produtos consumidos.</span>
            </label>
          </TabsContent>
          <TabsContent value="familia" className="space-y-4 pt-2">
            {form.familiares.map((fam, i) => (
              <div key={fam.id} className="rounded-xl border border-slate-200 p-4 space-y-3.5">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-slate-700">Familiar {i + 1}</p>
                  {form.familiares.length > 1 && (
                    <button onClick={() => removeFamiliar(i)} className="text-red-500 hover:text-red-600 flex items-center gap-1 text-sm">
                      <Trash2 className="w-4 h-4" /> Remover
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  <Field label="Nome"><input className={inputCls} value={fam.nome} onChange={(e) => setFam(i, "nome", e.target.value)} /></Field>
                  <Field label="Parentesco">
                    <Select value={fam.parentesco} onValueChange={(v) => setFam(i, "parentesco", v)}>
                      <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                      <SelectContent>{grausParentesco.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="CPF"><input className={inputCls} value={fam.cpf} placeholder="000.000.000-00" onChange={(e) => setFam(i, "cpf", maskCPF(e.target.value))} /></Field>
                  <Field label="RG"><input className={inputCls} value={fam.rg} placeholder="00.000.000-0" onChange={(e) => setFam(i, "rg", maskRG(e.target.value))} /></Field>
                  <Field label="Telefone"><input className={inputCls} value={fam.telefone} onChange={(e) => setFam(i, "telefone", e.target.value)} /></Field>
                  <Field label="E-mail"><input className={inputCls} value={fam.email} onChange={(e) => setFam(i, "email", e.target.value)} /></Field>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setUnico(i, "responsavelFinanceiro")}
                    className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors ${
                      fam.responsavelFinanceiro ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    <Star className="w-3.5 h-3.5" /> Responsável financeiro
                  </button>
                  <button
                    type="button"
                    onClick={() => setUnico(i, "contatoEmergencia")}
                    className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors ${
                      fam.contatoEmergencia ? "bg-red-500 text-white border-red-500" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    <Phone className="w-3.5 h-3.5" /> Contato de emergência
                  </button>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
                  <div className="col-span-1 sm:col-span-2">
                    <Field label="CEP">
                      <div className="relative">
                        <input className={inputCls} value={fam.endCep} placeholder="00000-000"
                          onChange={(e) => setFam(i, "endCep", maskCEP(e.target.value))}
                          onBlur={(e) => buscarCepFamiliar(i, e.target.value)} />
                        {cepLoading[i] && <LoaderCircle className="w-4 h-4 text-blue-500 animate-spin absolute right-3 top-1/2 -translate-y-1/2" />}
                      </div>
                    </Field>
                  </div>
                  <div className="col-span-1 sm:col-span-3"><Field label="Logradouro"><input className={inputCls} value={fam.endLogradouro} onChange={(e) => setFam(i, "endLogradouro", e.target.value)} /></Field></div>
                  <div className="col-span-1"><Field label="Nº"><input className={inputCls} value={fam.endNumero} onChange={(e) => setFam(i, "endNumero", e.target.value)} /></Field></div>
                  <div className="col-span-2 sm:col-span-2"><Field label="Bairro"><input className={inputCls} value={fam.endBairro} onChange={(e) => setFam(i, "endBairro", e.target.value)} /></Field></div>
                  <div className="col-span-1 sm:col-span-3"><Field label="Cidade"><input className={inputCls} value={fam.endCidade} onChange={(e) => setFam(i, "endCidade", e.target.value)} /></Field></div>
                  <div className="col-span-1"><Field label="UF"><input className={inputCls} value={fam.endUf} onChange={(e) => setFam(i, "endUf", e.target.value)} /></Field></div>
                </div>
              </div>
            ))}
            <Button variant="outline" className="gap-2 w-full" onClick={addFamiliar}>
              <Plus className="w-4 h-4" /> Adicionar familiar
            </Button>
          </TabsContent>
        </Tabs>

        <DialogFooter className="pt-2">
          <Button variant="outline" data-testid="residente-cancelar" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button className="bg-blue-600 hover:bg-blue-700 gap-2" data-testid="residente-salvar" onClick={handleSave}>
            <Save className="w-4 h-4" /> Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ResidenteFormDialog;
